import { useEffect, useMemo, useState } from 'react';
import {
  ComposedModal,
  ModalHeader,
  ModalBody,
  Button,
  TextInput,
  Dropdown,
  Layer,
} from '@carbon/react';
import { useAtom } from 'jotai';
import {
  tcCrossPlotChartModalOpenAtom,
  tcCrossPlotChartsAtom,
  tcEditingCrossPlotChartIdAtom,
} from './atoms';
import styles from './tc-cross-plot-chart-modal.module.scss';
import {
  CrossPlotAxis,
  CROSS_PLOT_AXIS_LABELS,
  CROSS_PLOT_AXIS_OPTIONS,
} from './utils';
import { CrossPlotChartConfig } from '../types';

interface AxisOption {
  id: CrossPlotAxis;
  label: string;
}

const DEFAULT_X: CrossPlotAxis = 'proppantIntensity';
const DEFAULT_Y: CrossPlotAxis = 'fluidIntensityBbl';

// Build the "Y vs X" chart title from the picked axes.
const buildAutoTitle = (x: CrossPlotAxis, y: CrossPlotAxis): string =>
  `${CROSS_PLOT_AXIS_LABELS[y]} vs ${CROSS_PLOT_AXIS_LABELS[x]}`;

// Generate a unique id for a new chart config.
const newChartId = (): string =>
  `cross-plot-${Date.now()}-${Math.round(Math.random() * 1_000_000)}`;

export const TcCrossPlotChartModal = () => {
  const [isOpen, setIsOpen] = useAtom(tcCrossPlotChartModalOpenAtom);
  const [editingId, setEditingId] = useAtom(tcEditingCrossPlotChartIdAtom);
  const [charts, setCharts] = useAtom(tcCrossPlotChartsAtom);

  const editingChart = useMemo<CrossPlotChartConfig | null>(
    () => charts.find((c) => c.id === editingId) ?? null,
    [charts, editingId]
  );

  const [title, setTitle] = useState('');
  const [xAxis, setXAxis] = useState<CrossPlotAxis>(DEFAULT_X);
  const [yAxis, setYAxis] = useState<CrossPlotAxis>(DEFAULT_Y);
  // Track whether the user has typed a custom title — once they have, we stop
  // overwriting it as they tweak the axes (mirrors the Apex add-chart UX).
  const [titleDirty, setTitleDirty] = useState(false);

  // Reset form state every time the modal opens. When editing, seed the form
  // from the chart being edited; otherwise pre-fill with the default axes.
  // A separate auto-title effect is intentionally avoided here — running two
  // effects on open caused the second to clobber the seeded title with the
  // initial default-axes auto-title. Title-axis mirroring is handled inline in
  // the dropdown onChange handlers below instead.
  useEffect(() => {
    if (!isOpen) return;
    if (editingChart) {
      // A previously-saved chart counts as "custom title" only if the user
      // actually typed something different from the auto-generated label.
      // This lets the title keep mirroring axes when it was auto-generated.
      const isAutoTitle =
        editingChart.title ===
        buildAutoTitle(editingChart.xAxis, editingChart.yAxis);
      setTitle(editingChart.title);
      setXAxis(editingChart.xAxis);
      setYAxis(editingChart.yAxis);
      setTitleDirty(!isAutoTitle);
    } else {
      setXAxis(DEFAULT_X);
      setYAxis(DEFAULT_Y);
      setTitle(buildAutoTitle(DEFAULT_X, DEFAULT_Y));
      setTitleDirty(false);
    }
  }, [isOpen, editingChart]);

  const handleXAxisChange = (newX: CrossPlotAxis) => {
    setXAxis(newX);
    if (!titleDirty) setTitle(buildAutoTitle(newX, yAxis));
  };

  const handleYAxisChange = (newY: CrossPlotAxis) => {
    setYAxis(newY);
    if (!titleDirty) setTitle(buildAutoTitle(xAxis, newY));
  };

  const handleClose = () => {
    setIsOpen(false);
    setEditingId(null);
  };

  const handleApply = () => {
    const trimmedTitle = title.trim() || buildAutoTitle(xAxis, yAxis);

    if (editingChart) {
      setCharts(
        charts.map((c) => {
          if (c.id !== editingChart.id) return c;
          return { ...c, title: trimmedTitle, xAxis, yAxis };
        })
      );
    } else {
      const next: CrossPlotChartConfig = {
        id: newChartId(),
        title: trimmedTitle,
        xAxis,
        yAxis,
        visible: true,
      };
      setCharts([...charts, next]);
    }

    handleClose();
  };

  const itemToString = (item: AxisOption | null) => (item ? item.label : '');

  const selectedX = CROSS_PLOT_AXIS_OPTIONS.find((o) => o.id === xAxis) ?? null;
  const selectedY = CROSS_PLOT_AXIS_OPTIONS.find((o) => o.id === yAxis) ?? null;

  return (
    <ComposedModal
      open={isOpen}
      onClose={handleClose}
      size="sm"
      className={styles.modal}
    >
      <ModalHeader
        title={editingChart ? 'Edit Cross Plot Chart' : 'Add Cross Plot Chart'}
      />
      <ModalBody>
        <div className={styles.content}>
          <Layer>
            <TextInput
              id="tc-cross-plot-chart-title"
              labelText="Chart Name"
              value={title}
              onChange={(e) => {
                setTitle(e.target.value);
                setTitleDirty(true);
              }}
              placeholder={buildAutoTitle(xAxis, yAxis)}
            />
          </Layer>

          <div className={styles.axisRow}>
            <Layer>
              <Dropdown
                id="tc-cross-plot-x-axis"
                titleText="X Axis"
                label="Select X axis"
                items={CROSS_PLOT_AXIS_OPTIONS}
                itemToString={itemToString}
                selectedItem={selectedX}
                onChange={({
                  selectedItem,
                }: {
                  selectedItem?: AxisOption | null;
                }) => {
                  if (selectedItem) handleXAxisChange(selectedItem.id);
                }}
              />
            </Layer>
            <Layer>
              <Dropdown
                id="tc-cross-plot-y-axis"
                titleText="Y Axis"
                label="Select Y axis"
                items={CROSS_PLOT_AXIS_OPTIONS}
                itemToString={itemToString}
                selectedItem={selectedY}
                onChange={({
                  selectedItem,
                }: {
                  selectedItem?: AxisOption | null;
                }) => {
                  if (selectedItem) handleYAxisChange(selectedItem.id);
                }}
              />
            </Layer>
          </div>
        </div>
      </ModalBody>
      <div className={styles.customFooter}>
        <Button
          size="lg"
          kind="ghost"
          className={styles.footerButton}
          onClick={handleClose}
        >
          Cancel
        </Button>
        <Button
          size="lg"
          kind="primary"
          className={styles.footerButton}
          onClick={handleApply}
          disabled={!title}
        >
          Apply
        </Button>
      </div>
    </ComposedModal>
  );
};
